import { readCookie, writeCookie } from './cookies.js';
import { isUuid, uuid } from './uuid.js';

/**
 * Sessions (spec §1 "session_id").
 *
 * `_td_sid` is NOT a session: the SDK writes it once and keeps it for 365 days, which
 * is why every query today has to rebuild sessions with window functions. This class
 * owns a separate `_td_ses` cookie that starts on the first interaction and expires
 * after 30 minutes of inactivity. `_td_sid` is never touched — the Postgres
 * attribution job joins orders on it.
 */

export const SESSION_COOKIE = '_td_ses';

export const DEFAULT_TTL_MS = 30 * 60 * 1000;

export class SessionManager {
  constructor({
    ttlMs = DEFAULT_TTL_MS,
    cookieName = SESSION_COOKIE,
    doc = typeof document !== 'undefined' ? document : null,
    now = () => Date.now(),
  } = {}) {
    this.ttlMs = ttlMs;
    this.cookieName = cookieName;
    this.doc = doc;
    this.now = now;
    this.state = null;
  }

  /**
   * Current session, renewing the inactivity window. Returns
   * `{ id, started, last, isNew }`.
   */
  touch() {
    const now = this.now();
    const stored = this.state || this.read();

    let state;
    if (stored && now - stored.last <= this.ttlMs) {
      state = { id: stored.id, started: stored.started, last: now, isNew: false };
    } else {
      state = { id: uuid(), started: now, last: now, isNew: true };
    }

    this.state = state;
    this.write(state);

    return state;
  }

  id() {
    return this.touch().id;
  }

  /** Session age in whole seconds — handy for offer_abandoned and funnel timing. */
  ageSeconds() {
    const state = this.state || this.touch();

    return Math.max(0, Math.round((this.now() - state.started) / 1000));
  }

  read() {
    if (!this.doc) {
      return null;
    }

    const raw = readCookie(this.cookieName, this.doc);
    if (!raw) {
      return null;
    }

    const [id, started, last] = raw.split('.');
    const startedMs = parseInt(started, 10);
    const lastMs = parseInt(last, 10);

    // The cookie is writable by any script on the domain; a forged id would end up in
    // the session_id column, and a future timestamp would keep the session alive
    // forever.
    if (!isUuid(id) || Number.isNaN(startedMs) || Number.isNaN(lastMs)) {
      return null;
    }

    if (startedMs > this.now() || lastMs > this.now()) {
      return null;
    }

    return { id, started: startedMs, last: lastMs };
  }

  write(state) {
    if (!this.doc) {
      return;
    }

    try {
      writeCookie(
        this.cookieName,
        [state.id, state.started, state.last].join('.'),
        Math.round(this.ttlMs / 1000),
        this.doc
      );
    } catch (e) {
      /* cookie-blocked browsers still get an in-memory session for this page load */
    }
  }
}
